/* eslint-disable replo/no-tailwind-literal-color */

import React, { useEffect, useState } from "react";

import { LinkPreviewPopoverHost } from "@editor/components/LinkPreviewPopoverHost";

import { fiberSelectedPayloadSchema } from "schemas/fileModifiers";

import ElementTypeTag from "./modifiers/ElementTypeTag";
import { useInlineContentEditor } from "./modifiers/hooks/useInlineContentEditor";
import InlineToolbar from "./modifiers/InlineToolbar";
import {
  useDefaultSelectedElementDesignation,
  useModifiersStore,
  useModifiersStoreActions,
} from "./modifiers/store/ModifiersStoreContext";
import PageRenderer from "./PageRenderer";

type ModifiablePageRendererProps = {
  pageRoute: string;
  className?: string;
  isInteractive?: boolean;
  showControls?: boolean;
  previewThumbnail?: boolean;
};

// NOTE (Kevin, 2025-09-27): Keep in sync with
// apps/replo-nextjs-scaffold/editor-scripts/scripts/notify-parent-pointerdown.ts
const CANVAS_POINTER_DOWN_MESSAGE = "replo-editor:canvas-pointerdown";
const CANVAS_KEYDOWN_MESSAGE = "replo-editor:canvas-keydown";

/**
 * A wrapper around PageRenderer that adds modifier functionality.
 * This component should only be used when modifier interactions are needed.
 */
function ModifiablePageRenderer({
  pageRoute,
  className,
  showControls = true,
  isInteractive = false,
  previewThumbnail,
}: ModifiablePageRendererProps) {
  const { displayMode } = useModifiersStore();
  const modifierActions = useModifiersStoreActions();

  const { updateText } = useInlineContentEditor();
  const defaultDesignation = useDefaultSelectedElementDesignation();

  const hoverRef = React.useRef<HTMLDivElement>(null);
  const clickedSectionRef = React.useRef<HTMLDivElement>(null);
  const modifiableIframeContainerRef = React.useRef<HTMLDivElement>(null);

  const [editBarStyles, setEditBarStyles] = useState<{
    top: number;
    left: number;
    display: string;
  }>({
    top: 0,
    left: 0,
    display: "none",
  });

  const [elementTagStyles, setElementTagStyles] = useState<{
    top: number;
    left: number;
    display: string;
  }>({
    top: 0,
    left: 0,
    display: "none",
  });

  const latestRectRef = React.useRef<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  /**
   * NOTE (Kurt, 2025-08-04): Clears selection in iframe when no element is selected.
   *
   * When the user deselects an element, this effect ensures that the iframe
   * is notified to clear its selection state to prevent stale element highlighting.
   */
  useEffect(() => {
    if (!defaultDesignation && showControls) {
      const iframe =
        modifiableIframeContainerRef.current?.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: "CLEAR_SELECTION" }, "*");
      }
    }
  }, [defaultDesignation, showControls]);

  /**
   * NOTE (Kurt, 2025-08-04): Synchronizes display mode between editor and preview iframe.
   *
   * When the editor switches between interactive and select modes, this effect
   * notifies the iframe so it can adjust its behavior accordingly. In select mode,
   * the iframe intercepts clicks for element selection; in interactive mode, it
   * allows normal user interactions.
   */
  useEffect(() => {
    const iframe =
      modifiableIframeContainerRef.current?.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "SET_DISPLAY_MODE",
          payload: { displayMode },
        },
        "*",
      );
    }
  }, [displayMode]);

  /**
   * NOTE (Kurt, 2025-08-04): Sets up message handling between editor and preview iframe.
   *
   * This effect listens for messages from the iframe and handles:
   * - FIBER_SELECTED: Updates selected element when user clicks in iframe
   * - HOVER_ELEMENT: Shows/hides blue dashed border around hovered elements
   * - SELECTED_ELEMENT_SCROLL_UPDATE: Updates element position for overlay positioning
   * - UPDATE_TEXT: Automatically saves text content when element loses focus
   *
   * Only active in select mode and when controls are visible.
   */
  /**
   * NOTE (Kevin, 2025-09-27): Additionally forwards iframe pointerdown messages so
   * popovers dismiss when clicking the preview canvas, regardless of display mode.
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const type = event.data?.type;

      if (type === CANVAS_POINTER_DOWN_MESSAGE) {
        const pointerType =
          typeof event.data.pointerType === "string"
            ? event.data.pointerType
            : "mouse";
        const eventTarget: EventTarget = document.body ?? document;

        // NOTE (Kevin, 2025-09-27): We need to dispatch a synthetic pointerdown event so Radix DismissableLayer observes it and dismisses popovers.
        const syntheticEvent =
          typeof PointerEvent === "function"
            ? new PointerEvent("pointerdown", {
                bubbles: true,
                cancelable: true,
                pointerType,
              })
            : new MouseEvent("mousedown", {
                bubbles: true,
                cancelable: true,
              });

        eventTarget.dispatchEvent(syntheticEvent);
        return;
      }

      if (type === CANVAS_KEYDOWN_MESSAGE) {
        const eventTarget: EventTarget = document.body ?? document;

        // NOTE (Kevin, 2025-09-29): Dispatch synthetic keyboard event to propagate keyboard shortcuts from iframe to parent.
        const syntheticKeyEvent = new KeyboardEvent("keydown", {
          bubbles: true,
          cancelable: true,
          key: event.data.key,
          code: event.data.code,
          metaKey: Boolean(event.data.metaKey),
          ctrlKey: Boolean(event.data.ctrlKey),
          shiftKey: Boolean(event.data.shiftKey),
          altKey: Boolean(event.data.altKey),
          repeat: Boolean(event.data.repeat),
        });

        eventTarget.dispatchEvent(syntheticKeyEvent);
        return;
      }

      if (!showControls || displayMode !== "select") {
        return;
      }

      if (type === "FIBER_SELECTED") {
        const payload = fiberSelectedPayloadSchema.parse(event.data.payload);
        modifierActions.setSelectedElementDesignations(payload);
        latestRectRef.current = event.data.payload;
        return;
      }

      if (type === "HOVER_ELEMENT" && hoverRef.current) {
        const rect = event.data.payload.rect;

        if (rect) {
          Object.assign(hoverRef.current.style, {
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            display: "block",
          });
        } else {
          hoverRef.current.style.display = "none";
        }
        return;
      }

      if (type === "SELECTED_ELEMENT_SCROLL_UPDATE") {
        const rect = event.data.payload.rect;
        if (rect) {
          latestRectRef.current = rect;
        }
        return;
      }

      if (type === "UPDATE_TEXT") {
        const { innerHTML } = event.data.payload;
        void updateText(innerHTML);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [displayMode, modifierActions, showControls, updateText]);

  /**
   * NOTE (Kurt, 2025-08-04): Continuously updates overlay positioning using requestAnimationFrame.
   *
   * This effect runs every frame to ensure the red border (selected element) and text edit
   * toolbar stay positioned correctly over the selected element. It reads from latestRectRef
   * (updated by message events) and applies smooth visual positioning to the overlay elements.
   *
   * Uses requestAnimationFrame for smooth 60fps updates, essential for responsive visual feedback.
   */
  useEffect(() => {
    if (!showControls) {
      return;
    }

    let animationFrameId: number;

    const updateOverlay = () => {
      const rect = latestRectRef.current;

      if (rect) {
        const { top, left, width, height } = rect;

        // Update red border if it exists
        if (clickedSectionRef.current) {
          Object.assign(clickedSectionRef.current.style, {
            top: `${top}px`,
            left: `${left}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: "block",
          });
        }

        // Update text edit bar styles
        if (
          defaultDesignation?.element.innerHtml &&
          defaultDesignation.element.innerHtml.trim() !== ""
        ) {
          setEditBarStyles({
            top: top + height + 8,
            left: left,
            display: "block",
          });
        } else {
          setEditBarStyles((prevStyles) => ({
            ...prevStyles,
            display: "none",
          }));
        }

        if (defaultDesignation) {
          setElementTagStyles({
            top: top - 30,
            left,
            display: "block",
          });
        } else {
          setElementTagStyles((prevStyles) => ({
            ...prevStyles,
            display: "none",
          }));
        }
      }
      animationFrameId = requestAnimationFrame(updateOverlay);
    };

    animationFrameId = requestAnimationFrame(updateOverlay);

    return () => cancelAnimationFrame(animationFrameId);
    /**
     * TODO (Kurt, 2025-08-05): Consider using useBoundingBox() hook instead of
     * manually managing position state with requestAnimationFrame. The useBoundingBox
     * hook provides automatic position tracking with mutation observers and optimized
     * updates. Not implementing now to avoid disrupting current positioning logic.
     */
  }, [showControls, defaultDesignation?.element.innerHtml, defaultDesignation]);

  // Create overlay elements when showControls is true
  const overlayElements = showControls ? (
    <>
      <div
        ref={hoverRef}
        className="absolute border-2 border-dashed border-blue-500 pointer-events-none"
        style={{
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          display: "none",
        }}
      />
      {defaultDesignation && (
        <div
          ref={clickedSectionRef}
          className="absolute border-2 border-blue-600 pointer-events-none"
          style={{
            top: 0,
            left: 0,
            width: 0,
            height: 0,
            display: "none",
          }}
        />
      )}
      {defaultDesignation?.element.innerHtml &&
      defaultDesignation.element.innerHtml.trim() !== "" ? (
        <div
          className="absolute pointer-events-auto z-10"
          style={{
            top: `${editBarStyles.top}px`,
            left: `${editBarStyles.left}px`,
            display: editBarStyles.display,
          }}
        >
          <InlineToolbar />
        </div>
      ) : null}
      {defaultDesignation && (
        <div
          className="absolute pointer-events-none z-20"
          style={{
            top: `${elementTagStyles.top}px`,
            left: `${elementTagStyles.left}px`,
            display: elementTagStyles.display,
          }}
        >
          <ElementTypeTag elementType={defaultDesignation.element.type} />
        </div>
      )}
    </>
  ) : undefined;

  return (
    <>
      <PageRenderer
        pageRoute={pageRoute}
        className={className}
        isInteractive={isInteractive}
        previewThumbnail={previewThumbnail}
        containerRef={modifiableIframeContainerRef}
        overlayElements={overlayElements}
      />
      <LinkPreviewPopoverHost />
    </>
  );
}

export default ModifiablePageRenderer;
